import { useTheme } from '@emotion/react';
import { useSetAtom } from 'jotai';
import React, { useCallback } from 'react';

import { Flex } from '~/components/blocks';
import { ActionButton } from '~/components/partials/ReceptionDrawer/ActionButton';
import { InactiveButton } from '~/components/partials/ReceptionDrawer/InactiveButton';
import { OutpatientQuestionnaireEntryDetailFragment } from '~/graphql/generated/graphql';
import {
  outpatientQuestionnaireFinishConfirmModalModalState,
  outpatientQuestionnaireNotifyConfirmModalState,
  outpatientQuestionnaireSendUnavailableModalState,
} from '~/state/reception/atoms';

import { useFinishOutpatientQuestionnaireEntry } from '../FinishConfirmModal/use-finish_outpatient_quetionnaire_entry';

type Props = {
  outpatientQuestionnaireEntry: OutpatientQuestionnaireEntryDetailFragment;
};

export const BookedButtons = (props: Props) => {
  const theme = useTheme();
  const { outpatientQuestionnaireEntry } = props;
  const isEntered = outpatientQuestionnaireEntry.status === 'entered';
  const lineVisitor = outpatientQuestionnaireEntry.lineVisitor;
  const messageSendable = lineVisitor?.messageSendable ?? false;

  const { finishOutpatientQuestionnaireEntry, finishing } = useFinishOutpatientQuestionnaireEntry();
  const setOutpatientQuestionnaireFinishConfirmModalState = useSetAtom(
    outpatientQuestionnaireFinishConfirmModalModalState,
  );
  const setOutpatientQuestionnaireSendUnavailableModalState = useSetAtom(
    outpatientQuestionnaireSendUnavailableModalState,
  );
  const setOutpatientQuestionnaireNotifyConfirmModalState = useSetAtom(
    outpatientQuestionnaireNotifyConfirmModalState,
  );
  const handleFinishClick = useCallback(async () => {
    if (outpatientQuestionnaireEntry.enableNotification && isEntered) {
      setOutpatientQuestionnaireFinishConfirmModalState((_state) => ({
        ..._state,
        isOpen: true,
        outpatientQuestionnaireEntryId: outpatientQuestionnaireEntry.id,
      }));
    } else {
      await finishOutpatientQuestionnaireEntry({
        variables: {
          input: {
            outpatientQuestionnaireEntryId: outpatientQuestionnaireEntry.id,
          },
        },
      });
    }
  }, [
    outpatientQuestionnaireEntry.enableNotification,
    setOutpatientQuestionnaireFinishConfirmModalState,
    outpatientQuestionnaireEntry.id,
    finishOutpatientQuestionnaireEntry,
    isEntered,
  ]);

  const handleNotificationClick = useCallback(() => {
    if (lineVisitor && !messageSendable) {
      setOutpatientQuestionnaireSendUnavailableModalState((_state) => ({
        ..._state,
        isOpen: true,
      }));
      return;
    }

    setOutpatientQuestionnaireNotifyConfirmModalState((_state) => ({
      ..._state,
      isOpen: true,
      outpatientQuestionnaireEntryId: outpatientQuestionnaireEntry.id,
    }));
  }, [
    lineVisitor,
    messageSendable,
    outpatientQuestionnaireEntry.id,
    setOutpatientQuestionnaireSendUnavailableModalState,
    setOutpatientQuestionnaireNotifyConfirmModalState,
  ]);

  if (outpatientQuestionnaireEntry.enableNotification) {
    return (
      <Flex flexDirection="column">
        {isEntered ? (
          <ActionButton
            use="primary"
            wide="fill"
            size="l"
            marginBottom={theme.space.m}
            onClick={handleNotificationClick}
          >
            準備完了通知を送信
          </ActionButton>
        ) : (
          <InactiveButton
            use="primary"
            status="送信済"
            wide="fill"
            size="l"
            marginBottom={theme.space.m}
          >
            準備完了通知を送信
          </InactiveButton>
        )}
        <ActionButton
          use="primaryInverse"
          wide="fill"
          size="l"
          onClick={handleFinishClick}
          loading={finishing}
        >
          服薬指導を完了
        </ActionButton>
      </Flex>
    );
  } else {
    return (
      <Flex alignItems="center" justifyContent="center">
        <ActionButton
          use="primary"
          wide="fill"
          size="l"
          onClick={handleFinishClick}
          loading={finishing}
        >
          対応完了
        </ActionButton>
      </Flex>
    );
  }
};
